import React, { useLayoutEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import cx from 'classnames';

import { usePreventBgScroll } from 'hooks/usePreventBgScroll';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { Portal } from 'components/shared/Portal';

import fadeTransition from 'styles/transitons/fade.module.sass';

import css from './popup.module.sass';

interface IProps {
  friendly?: boolean;
  zIndex?: number;
  popupClassName?: string;
  bodyClassName?: string;
  rootClassName?: string;
  isFullScreen?: boolean;
  isOverlayBlur?: boolean;
  isIndented?: boolean;
  isMobileStyle?: boolean;
  customId?: string;
  onClose?: () => void;
  onOverlayClick?: () => void;
}

export const Popup: React.FC<IProps> = ({
  friendly,
  zIndex,
  popupClassName,
  bodyClassName,
  rootClassName,
  isFullScreen,
  isOverlayBlur,
  isIndented = true,
  isMobileStyle,
  customId,
  children,
  onClose,
  onOverlayClick,
}) => {
  usePreventBgScroll({ enabled: true });

  useLayoutEffect(() => {
    const rootNode = document.getElementById('root');

    if (isOverlayBlur && rootNode) {
      rootNode.classList.add(css.blur);
    }

    return () => {
      if (isOverlayBlur && rootNode) {
        rootNode.classList.remove(css.blur);
      }
    };
  }, [isOverlayBlur]);

  return (
    <Portal>
      <div
        className={cx(css.root, rootClassName, {
          [css.rootTypeMobileStyle]: isMobileStyle,
        })}
        style={{ zIndex }}
        id={customId}
        onClick={onOverlayClick}
      >
        <CSSTransition in appear timeout={200} classNames={fadeTransition}>
          <section
            className={cx(css.popup, popupClassName, {
              [css.popupTypeFullScreen]: isFullScreen,
              [css.popupTypeIndented]: isIndented,
            })}
          >
            {onClose && (
              <BaseButton
                className={cx(css.closeBtn, { [css.friendly]: friendly })}
                type={ButtonTypes.Transparent}
                onClick={onClose}
              >
                <CloseIcon className={css.closeIcon} />
              </BaseButton>
            )}
            <div
              className={cx(css.body, bodyClassName, {
                [css.bodyTypeIndented]: isIndented,
              })}
            >
              {children}
            </div>
          </section>
        </CSSTransition>
      </div>
    </Portal>
  );
};
